import { styled } from "@monolith-forensics/monolith-ui";
import { useCaseFiles } from "../../Hooks";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { useUpdateCaseFile } from "../../files.query";
import {
  canDropInto,
  isManagedRow,
} from "@/Features/EvidenceFiles/evidence-files.utils";
import { EvidenceFilesTreeHint } from "@/Features/EvidenceFiles/EvidenceFilesTreeBanner";
import { FILE_SOURCE_TYPES } from "../../files.types";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  font-weight: 600;
`;

const StyledPathSegment = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  cursor: pointer;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;

  transition: color 0.05s linear;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  &.drop-target {
    font-weight: bold;
    background-color: ${({ theme }) => theme.palette.primary.main};
    border-radius: 4px;
    transition: background-color 0.2s ease;

    // disable pointer events for children
    & > * {
      pointer-events: none;
    }
  }
`;

const StyledCrumb = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

// Outside the link, so the Evidence Files (?) can sit between the name and
// the slash (ENG-2251). The margins keep the old spacing.
const StyledSeparator = styled.span`
  margin: 0 0.25rem;
`;

const PathSegment: React.FC<{
  segment: {
    uuid: string;
    name: string;
    path_order: number;
    href: string;
    source_type?: string | null;
  };
}> = ({ segment }) => {
  // Rows can't be moved into the managed Evidence Files tree (ENG-2133).
  const acceptsMoves = canDropInto(segment, { internal: true });
  const [isDropTarget, setIsDropTarget] = useState(false);
  const { refetchFiles } = useCaseFiles();
  const { mutate: updateFile } = useUpdateCaseFile({
    onSuccess: () => {
      refetchFiles();
    },
  });

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    // Check if the dragged item is a file/folder by checking the dataTransfer types
    if (e.dataTransfer.types.includes("text/plain") && acceptsMoves) {
      setIsDropTarget(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = acceptsMoves ? "move" : "none";
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only remove drop target state if we're actually leaving the element
    // and not just entering a child element
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDropTarget(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDropTarget(false);
    if (!acceptsMoves) return;

    try {
      const droppedData = JSON.parse(e.dataTransfer.getData("text/plain")) as {
        uuid: string;
        name: string;
        is_folder: boolean;
        source_type?: string | null;
      };
      if (isManagedRow(droppedData)) return;

      // Determine the target parent_uuid based on the segment
      const targetParentUuid = segment.uuid === "root" ? "root" : segment.uuid;

      updateFile({
        uuid: droppedData.uuid,
        parent_uuid: targetParentUuid,
      });
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  };

  const link = (
    <Link
      to={segment.href}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <StyledPathSegment className={isDropTarget ? "drop-target" : ""}>
        {segment.name}
      </StyledPathSegment>
    </Link>
  );

  return (
    <StyledCrumb>
      {segment.source_type === FILE_SOURCE_TYPES.EVIDENCE_ROOT_FOLDER ? (
        <EvidenceFilesTreeHint>{link}</EvidenceFilesTreeHint>
      ) : (
        link
      )}
      <StyledSeparator>/</StyledSeparator>
    </StyledCrumb>
  );
};

export const BreadCrumbs: React.FC = () => {
  const { currentPathArray } = useCaseFiles();
  const { case_id } = useParams();
  return (
    <StyledContainer>
      <PathSegment
        segment={{
          uuid: "root",
          name: "Case Files",
          path_order: 0,
          href: `/cases/${case_id}/documents/`,
        }}
      />
      {currentPathArray.map((item) => (
        <PathSegment
          key={item.uuid}
          segment={{
            ...item,
            href: `/cases/${case_id}/documents/folders/${item.uuid}`,
          }}
        />
      ))}
    </StyledContainer>
  );
};
